/*
 * Copyright (c) 2020. Legalbird eine Marke der R&S Internet Jewels GmbH
 */

import React from "react";
import guidebookContentStyle from "../guidebookContentStyle";
import QuestionsContactSection from "../QuestionsContactSection/QuestionsContactSection";
import DoubleButton from "../../Button/DoubleButton";
import HighlightBox from "../../Box/HighlightBox/HighlightBox";
import TextContainer from "../../Container/TextContainer";
import TeaserSlogan from "../../Teaser/TeaserSlogan";
import backgroundTeaser from "../../../assets/img/guidebook/Teaser_Ordnungswidrigkeitent_700px.jpg";
import { Helmet } from "react-helmet-async";
import Layout from "../../../containers/Layouts/Layout";
import Typography from "@mui/material/Typography";
import { Box, Link as MuiLink, Paper, TableHead } from "@mui/material";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import { gaSet } from "../../../services/googleAnalyticsService";
import { Link } from "react-router";

const GuidebookTableRowafficMinorOffences = () => {
  const title = "Ratgeber - Ordnungswidrigkeiten | Legalbird";

  gaSet({ title: title });

  return (
    <Layout backgroundType={"serviceworld"}>
      <Helmet>
        <title>{title}</title>
        <meta
          name="description"
          content="Sie erhalten hier einen Überblick darüber, welche Ordnungswidrigkeiten es im Bereich Verkehrsrecht gibt und mit welchen Strafen Sie rechnen müssen (Stand 09. November 2021 - alle Angaben ohne Gewähr)."
        />
      </Helmet>

      <Paper sx={guidebookContentStyle.paper}>
        <TeaserSlogan slogan={"Ordnungswidrigkeiten und ihre Strafen"} backgroundImagePath={backgroundTeaser} height={300} headingColor={"#333333"} />

        <TextContainer>
          <p>
            Sie erhalten hier einen Überblick darüber, welche Ordnungswidrigkeiten es im Bereich Verkehrsrecht gibt und mit welchen Strafen Sie rechnen müssen
            (Stand 09. November 2021 - alle Angaben ohne Gewähr).
          </p>
          <hr />
          <Typography variant={"h4"} sx={guidebookContentStyle.subheader}>
            Welche Ordnungswidrigkeiten gibt es?
          </Typography>
          <p>Die häufigsten Verstöße im Verkehrsrecht sind</p>
          <ol>
            <li>
              <MuiLink component={Link} sx={guidebookContentStyle.link} to={"#geschwindigkeitsverstoß"}>
                Zu schnell gefahren
              </MuiLink>
            </li>
            <li>
              <MuiLink component={Link} sx={guidebookContentStyle.link} to={"#parkvertoß"}>
                Falsches Halten oder Parken
              </MuiLink>
            </li>
            <li>
              <MuiLink component={Link} sx={guidebookContentStyle.link} to={"#abstandsverstoß"}>
                Zu wenig Abstand
              </MuiLink>
            </li>
            <li>
              <MuiLink component={Link} sx={guidebookContentStyle.link} to={"#rotlichtverstoß"}>
                Rote Ampel überfahren
              </MuiLink>
            </li>
            <li>
              <MuiLink component={Link} sx={guidebookContentStyle.link} to={"#handyverstoß"}>
                Handy am Steuer
              </MuiLink>
            </li>
            <li>
              <MuiLink component={Link} sx={guidebookContentStyle.link} to={"#drogenverstoß"}>
                Fahren unter Alkoholeinfluss
              </MuiLink>
            </li>
          </ol>
          <Box sx={guidebookContentStyle.scrollToAnchor} id={"geschwindigkeitsverstoß"} />
          <Typography variant={"h4"} sx={guidebookContentStyle.subheader}>
            Zu schnell gefahren - der Geschwindigkeitsverstoß
          </Typography>
          <p>
            Wenn Sie zu schnell gefahren sind, hängt die Höhe der Strafe davon ab, ob Sie innerorts oder außerhalb einer Ortschaft unterwegs waren. Allgemein
            gilt: wenn Sie innerorts zu schnell fahren, ist die Strafe höher, weil dort andere Verkehrsteilnehmer stärker gefährdet sind.
          </p>
          <p>
            Bei zu schnellem Fahren müssen Sie mit Bußgeld, Punkten und schlimmstenfalls auch mit einem Fahrverbot rechnen. Punkte in Flensburg fallen außer-
            und innerorts ab einem Tempoverstoß von 21 km/h an. Ein Fahrverbot droht Ihnen innerorts ab einer Geschwindigkeitsüberschreitung von 31 km/h,
            außerorts ab 41 km/h.
          </p>
          <p>
            Wichtig zu wissen: Wenn Sie innerhalb von einem Jahr mehrere Geschwindig­keits­überschreitungen begehen, kann die zuständige Behörde entweder direkt
            ein Fahrverbot verhängen oder die bereits verhängte Sperrfrist verlängern – mindestens um einen Monat.
          </p>
          <HighlightBox headline={"Strafe überhöhte Geschwindigkeit - innerorts"}>
            <Box sx={guidebookContentStyle.horizontalScroll}>
              <Table sx={guidebookContentStyle.tableGuide}>
                <TableHead>
                  <TableRow>
                    <TableCell>km/h zu schnell</TableCell>
                    <TableCell>Bußgeld</TableCell>
                    <TableCell>Punkte</TableCell>
                    <TableCell>Fahrverbot</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>Bis 10 km/h</TableCell>
                    <TableCell>30 €</TableCell>
                    <TableCell>-</TableCell>
                    <TableCell>-</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>11–15 km/h</TableCell>
                    <TableCell>50 €</TableCell>
                    <TableCell>-</TableCell>
                    <TableCell>-</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>16–20 km/h</TableCell>
                    <TableCell>70 €</TableCell>
                    <TableCell>-</TableCell>
                    <TableCell>-</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>21–25 km/h</TableCell>
                    <TableCell>115 €</TableCell>
                    <TableCell>1</TableCell>
                    <TableCell>-</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>26–30 km/h</TableCell>
                    <TableCell>180 €</TableCell>
                    <TableCell>1</TableCell>
                    <TableCell>1 Monat*</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>31–40 km/h</TableCell>
                    <TableCell>260 €</TableCell>
                    <TableCell>2</TableCell>
                    <TableCell>1 Monat</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>41–50 km/h</TableCell>
                    <TableCell>400 €</TableCell>
                    <TableCell>2</TableCell>
                    <TableCell>1 Monat</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>51–60 km/h</TableCell>
                    <TableCell>560 €</TableCell>
                    <TableCell>2</TableCell>
                    <TableCell>2 Monate</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>61–70 km/h</TableCell>
                    <TableCell>700 €</TableCell>
                    <TableCell>2</TableCell>
                    <TableCell>3 Monate</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Über 70 km/h</TableCell>
                    <TableCell>800 €</TableCell>
                    <TableCell>2</TableCell>
                    <TableCell>3 Monate</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
          </HighlightBox>
          <Typography>
            * Wenn Sie die Geschwindigkeit zweimal innerhalb eines Jahres um mindestens 26 km/h überschreiten, kann es auch zu einem Fahrverbot kommen.
          </Typography>
          <br />
          <HighlightBox headline={"Strafe überhöhte Geschwindigkeit - außerorts"}>
            <Box sx={guidebookContentStyle.horizontalScroll}>
              <Table sx={guidebookContentStyle.tableGuide}>
                <TableHead>
                  <TableRow>
                    <TableCell>km/h zu schnell</TableCell>
                    <TableCell>Bußgeld</TableCell>
                    <TableCell>Punkte</TableCell>
                    <TableCell>Fahrverbot</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>Bis 10 km/h</TableCell>
                    <TableCell>20 €</TableCell>
                    <TableCell>-</TableCell>
                    <TableCell>-</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>11–15 km/h</TableCell>
                    <TableCell>40 €</TableCell>
                    <TableCell>-</TableCell>
                    <TableCell>-</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>16–20 km/h</TableCell>
                    <TableCell>60 €</TableCell>
                    <TableCell>-</TableCell>
                    <TableCell>-</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>21–25 km/h</TableCell>
                    <TableCell>100 €</TableCell>
                    <TableCell>1</TableCell>
                    <TableCell>-</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>26–30 km/h</TableCell>
                    <TableCell>150 €</TableCell>
                    <TableCell>1</TableCell>
                    <TableCell>1 Monat*</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>31–40 km/h</TableCell>
                    <TableCell>200 €</TableCell>
                    <TableCell>1</TableCell>
                    <TableCell>1 Monat*</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>41–50 km/h</TableCell>
                    <TableCell>320 €</TableCell>
                    <TableCell>2</TableCell>
                    <TableCell>1 Monat</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>51–60 km/h</TableCell>
                    <TableCell>480 €</TableCell>
                    <TableCell>2</TableCell>
                    <TableCell>1 Monat</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>61–70 km/h</TableCell>
                    <TableCell>600 €</TableCell>
                    <TableCell>2</TableCell>
                    <TableCell>2 Monate</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Über 70 km/h</TableCell>
                    <TableCell>700 €</TableCell>
                    <TableCell>2</TableCell>
                    <TableCell>3 Monate</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
          </HighlightBox>
          <Typography>
            * Wenn Sie die Geschwindigkeit zweimal innerhalb eines Jahres um mindestens 26 km/h überschreiten, kann es auch zu einem Fahrverbot kommen.
          </Typography>
          <br />
          <hr />
          <Box sx={guidebookContentStyle.scrollToAnchor} id={"parkvertoß"} />
          <Typography variant={"h4"} sx={guidebookContentStyle.subheader}>
            Falsches Halten oder Parken
          </Typography>
          <p>
            Falschparken wird in der Regel mit einem Verwarnungsgeld geahndet. Wie hoch das Verwarngeld ist, hängt davon ab, wo genau Sie geparkt und ob Sie
            andere Verkehrsteilnehmer oder Anwohner behindert haben.
          </p>
          <p>
            Achtung: Zu häufiges Falschparken kann Folgen haben! Langjährige und hartnäckige Verkehrsverstöße rufen Zweifel an der Fahreignung hervor. Wenn Sie
            wiederholt falsch parken, können Sie daher unter Umständen aufgefordert werden eine sogenannte MPU, also eine medizinisch-psychologische
            Untersuchung durchführen zu lassen, von der abhängt, ob SIe Ihren Führerschein behalten dürfen. Sollten Sie sich der MPU entziehen, kann Ihnen
            ebenfalls die Fahrerlaubnis entzogen werden.
          </p>
          <hr />
          <Box sx={guidebookContentStyle.scrollToAnchor} id={"abstandsverstoß"} />
          <Typography variant={"h4"} sx={guidebookContentStyle.subheader}>
            Zu wenig Abstand - der Abstandsverstoß
          </Typography>
          <p>
            Damit Sie im Notfall genug Abstand zum Vordermann haben, muss eine Mindestdistanz eingehalten werden. Für den Sicherheitsabstand gibt die
            Straßenverkehrsordnung (StVO) folgende Richtwerte vor:
          </p>
          <Typography variant={"h5"}>Innerorts</Typography>
          <p>Eine Sekunde Abstand zum Vordermann - das entspricht z.B. bei 50 km/h 15 Metern oder 3 Fahrzeuglängen</p>
          <Typography variant={"h5"}>Außerorts (Überlandfahrt)</Typography>
          <p>Zwei Sekunden oder der halbe Tachowert - das entspricht z.B. bei 100 km/h einem Abstand von mindestens 50 Metern</p>
          <Typography variant={"h5"}>Für Lastwagen über 3,5 Tonnen Gewicht und Omnibusse</Typography>
          <p>Bereits ab 50 km/h gilt ein Sicherheitsabstand von mindestens 50 Metern.</p>
          <p>Wie hoch die Strafe ausfällt hängt davon ab</p>
          <ol>
            <li>wie schnell Sie gefahren sind</li>
            <li>wie nah Sie dem Vordermann aufgefahren sind, d.h. wie gering der Abstand war</li>
          </ol>
          <p>
            Unterhalb von 80 km/h Geschwindigkeit liegen die Bußgelder zwischen 25 und 35 Euro. Über 80 km/h drohen Ihnen nicht nur höhere Geldstrafen, sondern
            immer auch Punkte in Flensburg. Bei sehr hohen Geschwindigkeiten (über 100 km/h) müssen Sie zudem mit Fahrverbot rechnen.
          </p>
          <HighlightBox headline={"Ich verstehe meinen Bescheid nicht! Was heißt 5/10 vom halben Tachowert?"}>
            <p>
              Auf dem Bußgeldbescheid wird der Abstand im Verhältnis zu der gefahrenen Geschwindigkeit (dem Tachowert) angegeben. Ab 80 km/h sind Sie dazu
              verpflichtet, den halben Tachowert Abstand zu halten - wenn Sie 100 km/h fahren, müssen Sie also 50 Meter Abstand halten. Auf Ihrem Bescheid steht
              immer, um wie viel Sie den Mindestabstand (halber Tachowert) unterschritten haben.
            </p>
            <p>
              Beispiel:
              <br />
              Auf Ihrem Bußgeldbescheid steht, dass Sie bei 100km/h Geschwindigkeit nur 5/10 des halben Tachowerts Abstand gehalten haben.
            </p>
            <p>
              Halber Tachowert bei 100 km/h = 50m
              <br />
              5/10 des halben Tachowerts = 25m
            </p>
            <p>
              Heißt: Sie hätten 50 Meter Abstand halten müssen, haben aber nur 25 Meter Abstand gehalten. Sie haben also nur halb so viel Abstand gehalten, wie
              vom Gesetz vorgeschrieben.
            </p>
          </HighlightBox>
          <br />
          <HighlightBox headline={"Strafe Abstandsverstoß unter 80 km/h"}>
            <Box sx={guidebookContentStyle.horizontalScroll}>
              <Table sx={guidebookContentStyle.tableGuide}>
                <TableHead>
                  <TableRow>
                    <TableCell>Geschwindigkeit / Gefährdung / Beschädigung</TableCell>
                    <TableCell>Bußgeld</TableCell>
                    <TableCell>Punkte</TableCell>
                    <TableCell>Fahrverbot</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>Abstandsverstoß bei weniger als 80 km/h</TableCell>
                    <TableCell>25 €</TableCell>
                    <TableCell>-</TableCell>
                    <TableCell>-</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>+ Gefährdung</TableCell>
                    <TableCell>30 €</TableCell>
                    <TableCell>-</TableCell>
                    <TableCell>-</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>+ Beschädigung</TableCell>
                    <TableCell>35 €</TableCell>
                    <TableCell>-</TableCell>
                    <TableCell>-</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
          </HighlightBox>
          <br />
          <HighlightBox headline={"Strafe Abstandsverstoß 81 - 100 km/h"}>
            <Box sx={guidebookContentStyle.horizontalScroll}>
              <Table sx={guidebookContentStyle.tableGuide}>
                <TableHead>
                  <TableRow>
                    <TableCell>Abstand</TableCell>
                    <TableCell>Bußgeld</TableCell>
                    <TableCell>Punkte</TableCell>
                    <TableCell>Fahrverbot</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>5/10 des halben Tachowertes</TableCell>
                    <TableCell>75 €</TableCell>
                    <TableCell>1</TableCell>
                    <TableCell>-</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>4/10 des halben Tachowertes</TableCell>
                    <TableCell>100 €</TableCell>
                    <TableCell>1</TableCell>
                    <TableCell>-</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>3/10 des halben Tachowertes</TableCell>
                    <TableCell>160 €</TableCell>
                    <TableCell>1</TableCell>
                    <TableCell>-</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>2/10 des halben Tachowertes</TableCell>
                    <TableCell>240 €</TableCell>
                    <TableCell>1</TableCell>
                    <TableCell>-</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>1/10 des halben Tachowertes</TableCell>
                    <TableCell>320 €</TableCell>
                    <TableCell>1</TableCell>
                    <TableCell>-</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
          </HighlightBox>
          <br />
          <HighlightBox headline={"Strafe Abstandsverstoß 101 - 130 km/h"}>
            <Box sx={guidebookContentStyle.horizontalScroll}>
              <Table sx={guidebookContentStyle.tableGuide}>
                <TableHead>
                  <TableRow>
                    <TableCell>Abstand</TableCell>
                    <TableCell>Bußgeld</TableCell>
                    <TableCell>Punkte</TableCell>
                    <TableCell>Fahrverbot</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>5/10 des halben Tachowertes</TableCell>
                    <TableCell>75 €</TableCell>
                    <TableCell>1</TableCell>
                    <TableCell>-</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>4/10 des halben Tachowertes</TableCell>
                    <TableCell>100 €</TableCell>
                    <TableCell>1</TableCell>
                    <TableCell>-</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>3/10 des halben Tachowertes</TableCell>
                    <TableCell>160 €</TableCell>
                    <TableCell>2</TableCell>
                    <TableCell>1 Monat</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>2/10 des halben Tachowertes</TableCell>
                    <TableCell>240 €</TableCell>
                    <TableCell>2</TableCell>
                    <TableCell>2 Monate</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>1/10 des halben Tachowertes</TableCell>
                    <TableCell>320 €</TableCell>
                    <TableCell>2</TableCell>
                    <TableCell>3 Monate</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
          </HighlightBox>
          <br />
          <HighlightBox headline={"Strafe Abstandsverstoß über 130 km/h"}>
            <Box sx={guidebookContentStyle.horizontalScroll}>
              <Table sx={guidebookContentStyle.tableGuide}>
                <TableHead>
                  <TableRow>
                    <TableCell>Abstand</TableCell>
                    <TableCell>Bußgeld</TableCell>
                    <TableCell>Punkte</TableCell>
                    <TableCell>Fahrverbot</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>5/10 des halben Tachowertes</TableCell>
                    <TableCell>100 €</TableCell>
                    <TableCell>1</TableCell>
                    <TableCell>-</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>4/10 des halben Tachowertes</TableCell>
                    <TableCell>180 €</TableCell>
                    <TableCell>1</TableCell>
                    <TableCell>-</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>3/10 des halben Tachowertes</TableCell>
                    <TableCell>240 €</TableCell>
                    <TableCell>2</TableCell>
                    <TableCell>1 Monat</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>2/10 des halben Tachowertes</TableCell>
                    <TableCell>320 €</TableCell>
                    <TableCell>2</TableCell>
                    <TableCell>2 Monate</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>1/10 des halben Tachowertes</TableCell>
                    <TableCell>400 €</TableCell>
                    <TableCell>2</TableCell>
                    <TableCell>3 Monate</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
          </HighlightBox>
          <br />
          <hr />
          <Box sx={guidebookContentStyle.scrollToAnchor} id={"rotlichtverstoß"} />
          <Typography variant={"h4"} sx={guidebookContentStyle.subheader}>
            Rote Ampel überfahren - der Rotlichtverstoß
          </Typography>
          <p>
            Das Überfahren einer roten Ampel ist verboten. Wie hoch die Strafe ausfällt, hängt davon ab, wie lang die Ampel schon rot war. Ein einfacher
            Rotlichtverstoß liegt vor, wenn die <strong>Ampel nicht länger als 1 Sekunde lang rot</strong> gewesen ist. Bei einem qualifizierten Verstoß ist die{" "}
            <strong>Ampel schon länger als 1 Sekunde lang rot</strong> gewesen – oder es ist durch den Verstoß zu einer Sachbeschädigung oder Gefährdung, also
            zu einem Unfall, gekommen.
          </p>
          <p>Bei einem Rotlichtverstoß erhalten Sie immer Punkte in Flensburg und müssen Bußgeld zahlen. Zudem kann es auch zum Führerscheinentzug kommen.</p>
          <p>
            Wenn Sie in der Probezeit eine rote Ampel überfahren, müssen Sie auf eigene Kosten ein Aufbau­seminar besuchen. Machen Sie das nicht, kann es sein,
            dass Sie Ihren Führerschein auf Dauer verlieren. Außerdem verlängert sich Ihre Probezeit um 2 weitere Jahre.
          </p>
          <HighlightBox headline={"Strafe Rotlichtverstoß"}>
            <Box sx={guidebookContentStyle.horizontalScroll}>
              <Table sx={guidebookContentStyle.tableGuide}>
                <TableHead>
                  <TableRow>
                    <TableCell>Dauer / Gefährdung / Beschädigung</TableCell>
                    <TableCell>Bußgeld</TableCell>
                    <TableCell>Punkte</TableCell>
                    <TableCell>Fahrverbot</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>Innerhalb 1 Sekunde über die rote Ampel gefahren</TableCell>
                    <TableCell>90 €</TableCell>
                    <TableCell>1</TableCell>
                    <TableCell>-</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>+ Gefährdung</TableCell>
                    <TableCell>200 €</TableCell>
                    <TableCell>2</TableCell>
                    <TableCell>1 Monat</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>+ Beschädigung</TableCell>
                    <TableCell>240 €</TableCell>
                    <TableCell>2</TableCell>
                    <TableCell>1 Monat</TableCell>
                  </TableRow>
                  <Box component={"tr"} sx={guidebookContentStyle.verticalSpace} />
                  <TableRow>
                    <TableCell>Überfahren einer Ampel, die schon länger als 1 Sekunde rot ist</TableCell>
                    <TableCell>200 €</TableCell>
                    <TableCell>2</TableCell>
                    <TableCell>1 Monat*</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>+ Gefährdung</TableCell>
                    <TableCell>320 €</TableCell>
                    <TableCell>2</TableCell>
                    <TableCell>1 Monat*</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>+ Beschädigung</TableCell>
                    <TableCell>360 €</TableCell>
                    <TableCell>2</TableCell>
                    <TableCell>1 Monat*</TableCell>
                  </TableRow>
                  <Box component={"tr"} sx={guidebookContentStyle.verticalSpace} />
                  <TableRow>
                    <TableCell>An roter Ampel, an der ein grüner Rechtspfeil angebracht ist, nach rechts abgebogen, ohne vorher zu halten</TableCell>
                    <TableCell>70 €</TableCell>
                    <TableCell>1</TableCell>
                    <TableCell>-</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>+ Gefährdung</TableCell>
                    <TableCell>100 €</TableCell>
                    <TableCell>1</TableCell>
                    <TableCell>-</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>+ Verursachung eines Unfalls</TableCell>
                    <TableCell>120 €</TableCell>
                    <TableCell>1</TableCell>
                    <TableCell>-</TableCell>
                  </TableRow>
                  <Box component={"tr"} sx={guidebookContentStyle.verticalSpace} />
                  <TableRow>
                    <TableCell>
                      An roter Ampel, an der ein grüner Rechtspfeil angebracht ist, nach rechts abgebogen, ohne vorher zu halten und dabei den Fußgänger- oder
                      Fahrradverkehr der freigegebenen Verkehrsrichtung behindert
                    </TableCell>
                    <TableCell>100 €</TableCell>
                    <TableCell>1</TableCell>
                    <TableCell>-</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
          </HighlightBox>
          <p>* je nach Tatbegehung Geldstrafe, Führerscheinentzug und Freiheitsstrafe bis 5 Jahre gemäß §315c StGB möglich</p>
          <hr />
          <Box sx={guidebookContentStyle.scrollToAnchor} id={"handyverstoß"} />
          <Typography variant={"h4"} sx={guidebookContentStyle.subheader}>
            Handy am Steuer
          </Typography>
          <p>
            Das Handy am Steuer zu nutzen ist allgemein verboten. Das bedeutet in erster Linie, dass Sie während der Fahrt kein „Handy in der Hand haben
            dürfen“. Das gilt auch für Tablets oder E-Books. Unter das Handyverbot fällt nicht nur das bloße Telefonieren, sondern auch andere Formen der
            elektronischen Kommunikation wie Mails tippen, Messages verschicken oder Surfen im Internet. Erlaubt ist nur das Telefonieren über eine
            Freisprechanlage.
          </p>
          <p>
            Viele Autofahrer denken, sie dürfen an einem Standstreifen halten und von dort aus ein Telefonat führen. Dies ist ein Irrtum! Hier gilt sogar eine
            doppelte Strafe: Sie werden wegen des Handyverbots am Steuer und wegen des Halteverbots am Standstreifen bestraft.
          </p>
          <HighlightBox headline={"Auch Fahrradfahrer und E-Scooter-Fahrer sind betroffen!"}>
            <Box sx={guidebookContentStyle.centered}>
              <p>
                Das Handyverbot gilt nicht nur für Autofahrer, sondern allgemein für alle Fahrer. Darunter versteht die Straßenverkehrsordnung nicht nur
                Autofahrer, sondern zum Beispiel auch Fahrradfahrer und E-Scooter-Fahrer.
              </p>
            </Box>
          </HighlightBox>
          <p>
            Als Autofahrer müssen Sie mit 100 Euro Geldbuße und einem Punkt in Flensburg rechnen. Bei schweren Verstößen können es auch bis zu 200 Euro, 2
            Punkte und ein Fahrverbot werden.
          </p>
          <p>Auch bei Fahrradfahrern greift der Staat nun härter durch: Wenn Sie als Radfahrer während der Fahrt Ihr Handy nutzen, müssen Sie 55 € zahlen.</p>
          <p>
            Wenn Sie öfter mit dem Handy am Steuer erwischt werden, werden die STableRowafen deutlich höher. Sie müssen dann nicht nur Bußgeld zahlen, sondern
            erhalten auch ein Fahrverbot von bis zu 3 Monaten.
          </p>
          <hr />
          <Box sx={guidebookContentStyle.scrollToAnchor} id={"drogenverstoß"} />
          <Typography variant={"h4"} sx={guidebookContentStyle.subheader}>
            Fahren unter Alkoholeinfluss
          </Typography>
          <p>
            Alkohol am Steuer ist sehr schnell eine Straftat und wird dann auch entsprechend stärker bestraft. Nur bis zu einem gewissen Maß gilt Alkohol am
            Steuer als eine Ordnungswidrigkeit
          </p>
          <p>
            Wenn Sie mit 0,5 bis 1,09 Promille von der Polizei angehalten werden, aber immer noch klar ansprechbar sind, keine Ausfallerscheinungen zeigen und
            keinen Unfall verursacht haben, gilt das als relative Fahruntüchtigkeit. Dieser Alkoholverstoß ist eine Ordnungswidrigkeit und wird unterschiedlich
            stark bestraft – je nachdem, ob Sie Ersttäter sind bzw. wie oft Sie schon erwischt worden sind.
          </p>
          <p>Strafen bei einer relativen Fahruntüchtigkeit</p>
          <ol>
            <li>Ersttäter: 500 Euro Bußgeld, 2 Punkte und einen Monat Fahrverbot</li>
            <li>Beim zweiten Vergehen: 1.000 Euro Bußgeld, 2 Punkte und 3 Monate Fahrverbot</li>
            <li>Beim dritten Vergehen: 1.500 Euro Bußgeld, 2 Punkte und 3 Monate Fahrverbot</li>
          </ol>
          <p>
            Werden Sie in der Probezeit auch nur mit einem geringen Blutalkoholwert (unter 0,5 Promille) angehalten, drohen bereits heftige Konsequenzen wie
            Bußgeld, Punkte, Probezeitverlängerung und der Entzug der Fahrerlaubnis.
          </p>
          <HighlightBox headline={"Alkohol am Steuer wird schnell zur Straftat!"}>
            <Box sx={guidebookContentStyle.centered}>
              <p>
                Ab 1,1 Promille spricht man von absoluter Fahruntüchtigkeit. Dies ist eine Straftat und wird entsprechend hart geahndet. Sie müssen mit einer
                sehr hohen Geldstrafe oder schlimmstenfalls sogar einer Freiheitsstrafe rechnen. Zudem wird Ihnen die Fahrerlaubnis entzogen und eine Sperrfrist
                für die Wiedererteilung von 8-12 Monaten verhängt. In schwer wiegenden Fällen oder bei Wiederholungstaten droht der lebenslange Entzug der
                Fahrerlaubnis.
              </p>
            </Box>
          </HighlightBox>
          <br />
          <Typography variant={"h4"} sx={guidebookContentStyle.subheader}>
            Jetzt professionell gegen Ihren Bußgeldbescheid vorgehen!
          </Typography>
          <p>
            Ihnen droht eine Strafe oder sogar der Verlust Ihres Führerscheins und das möchten Sie verhindern? Wir sind Spezialisten im Verkehrsrecht und prüfen
            Ihren Fall kostenlos. Ihr Bescheid kann angefochten werden? Dann setzen wir uns mit vollem Einsatz dafür ein, dass Ihre Strafe verhindert oder
            zumindest reduziert wird!
          </p>
          <DoubleButton
            buttonText={"Kostenlose Ersteinschätzung"}
            buttonLink={"/servicewelt"}
            backText={"Zurück zum Ratgeber"}
            backLink={"/ratgeber/verkehrsrecht"}
          />
          <QuestionsContactSection />
        </TextContainer>
      </Paper>
    </Layout>
  );
};

export default GuidebookTableRowafficMinorOffences;
